import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Label,
  RadioGroup,
  RadioGroupItem,
} from "@repo/ui";

interface IntroModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onDecline: () => void;
  isLoading: boolean;
}

export function IntroModal({
  isOpen,
  onConfirm,
  onDecline,
  isLoading,
}: IntroModalProps) {
  const [q1Response, setQ1Response] = useState<boolean | null>(null);
  const [q2Response, setQ2Response] = useState<boolean | null>(null);

  // Peut commencer seulement si les 2 réponses sont OUI
  const canStart = q1Response === true && q2Response === true;
  // A refusé si une réponse est NON
  const hasDeclined = q1Response === false || q2Response === false;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
        <CardHeader className="text-center flex-shrink-0">
          {/* Logo Aix-Marseille Université */}
          {/* <div className="mx-auto mb-4">
            <img
              src="/logos/aix-marseille-universite-logo.jpg"
              alt="Aix-Marseille Université"
              className="w-24 h-24 object-contain mx-auto"
            />
          </div> */}
          <CardTitle className="text-2xl">Notice d'information</CardTitle>
          <CardDescription className="text-base mt-2">
            Entretien structuré
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-4 overflow-y-auto flex-1">
          {/* Introduction */}
          <p className="font-medium">Madame, Mademoiselle, Monsieur,</p>

          <p>
            Vous allez passer un entretien dans le cadre du processus de
            recrutement pour le poste auquel vous avez candidaté. Il s'agit d'un
            <span className="font-bold"> entretien structuré</span>, dans lequel
            les questions :
          </p>

          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>évaluent uniquement des compétences requises pour le poste</li>
            <li>sont identiques pour tous les candidats</li>
          </ul>

          <p>
            La version automatisée de cet entretien, que vous allez passer, fait
            l'objet d'une recherche réalisée par le Laboratoire Lorrain de
            Psychologie et Neurosciences (2LPN) et supervisée par Vincent
            Berthet, maître de conférences à l'université de Lorraine. Pour
            toute demande d'informations, vous pouvez envoyer un email à :{" "}
            <a
              href="mailto:vincent.berthet@univ-lorraine.fr"
              className="text-primary underline"
            >
              vincent.berthet@univ-lorraine.fr
            </a>
          </p>

          {/* Objectif de la recherche */}
          <div className="mt-6">
            <h3 className="font-semibold text-foreground">
              Objectif de la recherche
            </h3>
            <p className="mt-2">
              Cette recherche vise à tester une version automatisée de
              l'entretien structuré.
            </p>
          </div>

          {/* Déroulement */}
          <div className="mt-6">
            <h3 className="font-semibold text-foreground">
              Comment se déroule l'étude ?
            </h3>
            <p className="mt-2">
              Il vous suffit de répondre aux questions qui vous seront posées.
              L'entretien dure 20 min environ.
            </p>
            <p className="mt-2 font-bold">
              Nous vous décourageons fortement de recourir à une IA, cet usage
              pourra être détecté et susceptible de vous pénaliser.
            </p>
            <p className="mt-2">
              Les données recueillies lors de cette recherche seront exploitées
              uniquement dans le cadre de ce recrutement et à des fins de
              recherche de façon strictement confidentielle.
            </p>
          </div>

          {/* Droits */}
          <div className="mt-6">
            <h3 className="font-semibold text-foreground">
              Quels sont vos droits en tant que participant(e) à cette recherche
              ?
            </h3>
            <p className="mt-2">
              Vous pouvez refuser de participer à cette recherche sans avoir à
              vous justifier.
            </p>
          </div>

          {/* Questions de consentement */}
          <div className="mt-8 pt-6 border-t space-y-6">
            <h3 className="font-semibold text-foreground text-center">
              Consentement
            </h3>

            {/* Question 1 */}
            <div className="space-y-3">
              <p className="font-medium text-foreground">
                J'ai pris connaissance des informations m'expliquant le
                protocole de recherche mentionné ci-dessus :
              </p>
              <RadioGroup
                value={q1Response === null ? "" : q1Response ? "oui" : "non"}
                onValueChange={(value) => setQ1Response(value === "oui")}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="oui" id="q1-oui" />
                  <Label htmlFor="q1-oui" className="cursor-pointer">
                    OUI
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="non" id="q1-non" />
                  <Label htmlFor="q1-non" className="cursor-pointer">
                    NON
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Question 2 */}
            <div className="space-y-3">
              <p className="font-medium text-foreground">
                Je consens à passer cet entretien :
              </p>
              <RadioGroup
                value={q2Response === null ? "" : q2Response ? "oui" : "non"}
                onValueChange={(value) => setQ2Response(value === "oui")}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="oui" id="q2-oui" />
                  <Label htmlFor="q2-oui" className="cursor-pointer">
                    OUI
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="non" id="q2-non" />
                  <Label htmlFor="q2-non" className="cursor-pointer">
                    NON
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 sm:justify-end flex-shrink-0">
          <Button
            variant="outline"
            onClick={onDecline}
            disabled={isLoading}
            className="min-w-32"
          >
            Refuser
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!canStart || isLoading}
            className="min-w-40"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Démarrage en cours...
              </>
            ) : (
              "Commencer l'entretien"
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>,
    document.body,
  );
}
